import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { ManageFieldsService } from '@app/feature/manage-fields/manage-fields.service'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { Field, FieldEntities, FieldType, Workspace } from '@models/ui'
import { UntilDestroy } from '@ngneat/until-destroy'
import { FieldTypeFacadeService } from '@services/store-facade'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { TbIconComponent } from '../../@components-library/tb-icon/tb-icon.component'
import { TypedTemplateDirective } from '../typed-template.directive'
import { TbButtonComponent } from '../../@components-library/tb-button/tb-button.component'
import { TbDividerComponent } from '../../@components-library/tb-divider/tb-divider.component'
import { NgTemplateOutlet, AsyncPipe } from '@angular/common'
import { ModalLayoutComponent } from '../../@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { TranslocoModule } from '@jsverse/transloco'
import { ModalLayoutButtonsComponent } from '@components-library/tb-modal-manager/modal-layout-buttons/modal-layout-buttons.component'

export interface FieldTypeSelectorData {
    selectedWorkspace?: Workspace
    newWorkspace?: boolean
    existingFields?: FieldEntities
}

interface FieldTypeSelectorFields {
    onePerWorkspace: FieldType[]
    multiple: FieldType[]
}

@UntilDestroy()
@Component({
    selector: 'app-field-type-selector',
    templateUrl: './field-type-selector.component.html',
    imports: [
        TranslocoModule,
        ModalLayoutComponent,
        NgTemplateOutlet,
        TbDividerComponent,
        TbButtonComponent,
        TypedTemplateDirective,
        TbIconComponent,
        AsyncPipe,
        ModalLayoutButtonsComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldTypeSelectorComponent extends ModalContainerComponent<
    FieldTypeSelectorData,
    Field | undefined
> {
    private readonly fieldTypeFacadeService = inject(FieldTypeFacadeService)
    private readonly manageFieldsService = inject(ManageFieldsService)

    fieldTypes$: Observable<FieldTypeSelectorFields> = this.getFieldTypes().pipe(
        map(this.filterFieldTypes),
    )

    contextType = {} as { fieldTypes: FieldType[]; onePerWorkspace: boolean }

    addField(type: FieldType, onePerWorkspace: boolean) {
        this.manageFieldsService
            .addField(
                {
                    field_type_code: type.guid,
                    workspace_guid: this.data.selectedWorkspace?.guid,
                    new_workspace: this.data.newWorkspace,
                    existing_fields: this.data.existingFields,
                    one_per_workspace: onePerWorkspace,
                },
                this.modalManagerService,
            )
            .subscribe((field) => {
                this.close(field)
            })
    }

    private filterFieldTypes(fieldTypes: FieldType[]) {
        return fieldTypes.reduce(
            (res: FieldTypeSelectorFields, type: FieldType) => {
                if (type.operation_code_entity.multiple) {
                    res.multiple.push(type)
                } else {
                    res.onePerWorkspace.push(type)
                }
                return res
            },
            { onePerWorkspace: [], multiple: [] },
        )
    }

    private getFieldTypes() {
        if (!this.data.newWorkspace)
            return this.fieldTypeFacadeService.selectAvailableFieldTypeByWorkspace$

        return this.fieldTypeFacadeService.selectAvailableFieldTypesForNewWorkspace$(
            this.data.existingFields!,
        )
    }
}
